import { useRef, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowRightLeft, Camera, X } from 'lucide-react';
import { toast } from 'sonner';
import type { Asset, HolderInput } from '@khadi/shared';
import { api, API_BASE, ApiError } from '../lib/api';
import { verkleinern } from '../lib/bilder';
import { useStatuses } from '../lib/queries';
import { HolderPicker, isHolderComplete } from './HolderPicker';
import { cn } from './ui';

/**
 * Übergeben, zurücknehmen, ausmustern — ein Dialog für alles.
 *
 * Der Status ergibt sich aus dem Ziel und muss nicht mitgedacht werden;
 * nur beim Ausmustern wird er ausdrücklich gewählt.
 */
export function MoveSheet({ asset, onClose }: { asset: Asset; onClose: () => void }) {
  const queryClient = useQueryClient();
  const statuses = useStatuses();
  const [target, setTarget] = useState<HolderInput>({ type: 'employee', id: 0 } as HolderInput);
  const [note, setNote] = useState('');
  const [requestConfirmation, setRequestConfirmation] = useState(false);
  const [archiveStatusId, setArchiveStatusId] = useState<number | ''>('');
  const [fotos, setFotos] = useState<File[]>([]);
  const fotoRef = useRef<HTMLInputElement>(null);

  const archiveStatuses = statuses.data?.rows.filter((status) => status.is_archived) ?? [];
  const isArchiving = archiveStatusId !== '';

  const move = useMutation({
    mutationFn: async () => {
      const updated = await api<Asset>(`/assets/${asset.id}/move`, {
        method: 'POST',
        body: {
          to: isArchiving ? { type: 'none' } : target,
          status_id: isArchiving ? archiveStatusId : undefined,
          note: note.trim() || undefined,
          request_confirmation: requestConfirmation && target.type === 'employee',
        },
      });

      // Fotos gehören zum Vorgang, nicht zum Gerät im Allgemeinen — deshalb
      // erst jetzt hochladen, wenn die Bewegung eine Kennung hat.
      if (fotos.length > 0) {
        const verlauf = await api<{ rows: { id: number }[] }>(`/assets/${asset.id}/movements`);
        const vorgang = verlauf.rows[0]?.id;

        for (const datei of fotos) {
          const daten = new FormData();
          if (vorgang) daten.append('movement_id', String(vorgang));
          daten.append('note', isArchiving ? 'Zustand bei Ausmusterung' : 'Zustand bei Übergabe');
          daten.append('file', await verkleinern(datei), datei.name);

          await fetch(`${API_BASE}/assets/${asset.id}/photos`, {
            method: 'POST',
            body: daten,
            credentials: 'include',
          });
        }
      }

      return updated;
    },
    onSuccess: (updated) => {
      void queryClient.invalidateQueries({ queryKey: ['asset', asset.id] });
      void queryClient.invalidateQueries({ queryKey: ['asset-movements', asset.id] });
      void queryClient.invalidateQueries({ queryKey: ['asset-photos', asset.id] });
      void queryClient.invalidateQueries({ queryKey: ['assets'] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard'] });
      toast.success(
        updated.holder
          ? `${updated.asset_tag} ist jetzt bei ${updated.holder.name}`
          : `${updated.asset_tag} ist zurück im Lager`,
      );
      onClose();
    },
    onError: (error) => {
      toast.error(error instanceof ApiError ? error.message : 'Die Übergabe ist fehlgeschlagen');
    },
  });

  const ready = isArchiving || isHolderComplete(target);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-[var(--color-khadi-forest-900)]/45 backdrop-blur-[2px] sm:items-center sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Gerät übergeben"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="animate-sheet-up safe-bottom max-h-[92dvh] w-full max-w-md overflow-y-auto rounded-t-[var(--radius-xl)] border border-[var(--color-line)] bg-[var(--color-bg-elevated)] p-5 shadow-[var(--shadow-lg)] sm:rounded-[var(--radius-xl)]">
        <div className="mb-1 flex items-center justify-between">
          <h2 className="font-[family-name:var(--font-display)] text-2xl">Gerät übergeben</h2>
          <button type="button" onClick={onClose} className="btn btn-quiet btn-sm" aria-label="Schließen">
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>

        <p className="mb-4 text-sm text-[var(--color-fg3)]">
          <span className="tabular font-semibold text-[var(--color-fg1)]">{asset.asset_tag}</span>{' '}
          {asset.model?.name} · derzeit{' '}
          {asset.holder ? `bei ${asset.holder.name}` : 'im Lager'}
        </p>

        {!isArchiving && (
          <HolderPicker value={target} onChange={setTarget} />
        )}

        {archiveStatuses.length > 0 && (
          <details
            className="mt-4 rounded-[var(--radius-md)] border border-[var(--color-line)] px-5 py-4"
            open={isArchiving}
          >
            <summary className="cursor-pointer text-sm text-[var(--color-fg2)]">
              Stattdessen ausmustern
            </summary>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <button
                type="button"
                onClick={() => setArchiveStatusId('')}
                className={cn(
                  'rounded-[var(--radius-md)] border px-3 py-1.5 text-sm transition-colors',
                  !isArchiving
                    ? 'border-[var(--color-khadi-forest-800)] bg-[var(--color-khadi-forest-800)] text-[var(--color-khadi-cream-50)]'
                    : 'border-[var(--color-line)]',
                )}
              >
                Nicht ausmustern
              </button>
              {archiveStatuses.map((status) => (
                <button
                  key={status.id}
                  type="button"
                  onClick={() => setArchiveStatusId(status.id)}
                  className={cn(
                    'rounded-[var(--radius-md)] border px-3 py-1.5 text-sm transition-colors',
                    archiveStatusId === status.id
                      ? 'border-[var(--color-accent)] bg-[var(--color-khadi-clay-100)] font-semibold text-[var(--color-khadi-clay-700)]'
                      : 'border-[var(--color-line)] hover:bg-[var(--color-bg-tinted)]',
                  )}
                >
                  {status.name}
                </button>
              ))}
            </div>
          </details>
        )}

        {/* Zustand festhalten — gerade beim Ausmustern und bei Rückläufern ist
            später nicht mehr zu klären, wie das Gerät ankam. */}
        <div className="mt-4">
          <span className="label">
            Zustand dokumentieren{' '}
            <span className="font-normal normal-case">
              {isArchiving ? '(empfohlen beim Ausmustern)' : '(optional)'}
            </span>
          </span>

          <input
            ref={fotoRef}
            type="file"
            accept="image/*"
            capture="environment"
            multiple
            className="hidden"
            onChange={(event) => {
              setFotos([...fotos, ...Array.from(event.target.files ?? [])]);
              event.target.value = '';
            }}
          />

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => fotoRef.current?.click()}
              className="btn btn-ghost btn-md"
            >
              <Camera className="h-4 w-4" aria-hidden />
              Foto aufnehmen
            </button>
            {fotos.length > 0 && (
              <>
                <span className="text-sm text-[var(--color-fg2)]">
                  {fotos.length} {fotos.length === 1 ? 'Bild' : 'Bilder'} vorgemerkt
                </span>
                <button
                  type="button"
                  onClick={() => setFotos([])}
                  className="btn btn-quiet btn-sm"
                >
                  verwerfen
                </button>
              </>
            )}
          </div>
        </div>

        <div className="mt-4">
          <label className="label" htmlFor="move-note">
            Notiz <span className="font-normal normal-case">(optional)</span>
          </label>
          <textarea
            id="move-note"
            rows={2}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            className="field"
            placeholder="z. B. Austausch wegen Defekt"
          />
        </div>

        {target.type === 'employee' && !isArchiving && (
          <label className="mt-3 flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={requestConfirmation}
              onChange={(event) => setRequestConfirmation(event.target.checked)}
              className="mt-0.5 h-4 w-4 accent-[var(--color-accent)]"
            />
            <span>
              Empfangsbestätigung per E-Mail anfordern
              <span className="block text-sm text-[var(--color-fg3)]">
                Das Gerät gilt trotzdem sofort als übergeben.
              </span>
            </span>
          </label>
        )}

        <button
          type="button"
          onClick={() => move.mutate()}
          disabled={!ready || move.isPending}
          className="btn btn-primary btn-lg mt-5 w-full"
        >
          <ArrowRightLeft className="h-4 w-4" aria-hidden />
          {move.isPending ? 'Wird gebucht …' : isArchiving ? 'Ausmustern' : 'Übergeben'}
        </button>
      </div>
    </div>
  );
}
